import type { BrowserTask, PublishPayload } from "@ondit/publishing";
import type {
	DistributionChannel,
	DistributionPost,
	DistributionSource,
	PageDistributionRow,
	PublishingPlatform,
} from "~/core/api/distribution";
/**
 * The panel's client for the ondit API.
 *
 * Errors carry the server's `message_key`, and the panel translates it. That is
 * why the server never sends English prose: the same refusal has to read
 * naturally in both languages, and only the client knows which one the reader
 * chose.
 */

import { ApiError, send } from "~/core/api/transport";

export { announceGate, GATE_EVENT, type GateReason } from "~/core/api/transport";
export { ApiError };

/** Exported because the crawler setup snippet has to name it in copyable text. */
export const ORIGIN = import.meta.env.VITE_API_ORIGIN || "";

/** A refusal reported beside what it refuses: its sentence's key and the values that fill it. */
export interface Refusal {
	message_key: string;
	params?: Record<string, string | number>;
}

/**
 * The panel's paths.
 *
 * Sign-in lives at `/auth/*` rather than under `/api/v1`, because it is what a
 * browser is redirected to and back from, and a version prefix on a URL people
 * bookmark buys nothing. A path starting `/auth/` skips the prefix; everything
 * else is panel API.
 */
export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
	const response = await send(`${ORIGIN}${path.startsWith("/auth/") ? "" : "/api/v1"}${path}`, {
		...init,
		headers: { accept: "application/json", ...init.headers },
	});
	if (response.status === 204) return undefined as T;

	const text = await response.text();
	try {
		return (text ? JSON.parse(text) : null) as T;
	} catch {
		return null as T;
	}
}

const get = <T>(path: string) => request<T>(path);
export const post = <T>(path: string, body?: unknown) =>
	request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) => request<T>(path, { method: "PATCH", body: JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: "DELETE" });

/**
 * Build a query string from a plain object.
 *
 * Typed as `object` rather than `Record<string, ...>` on purpose: requiring a
 * Record would force an index signature onto `Filters`, and an interface with
 * an index signature accepts `{ rnge: "7d" }` without complaint. Closed
 * interfaces in, runtime guard here.
 */
function query(params: object): string {
	const search = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value === undefined || value === null || value === "") continue;
		search.set(key, String(value));
	}
	const q = search.toString();
	return q ? `?${q}` : "";
}

export interface Usage {
	days: number;
	total_micros: number;
	models: {
		model: string;
		calls: number;
		input_tokens: number;
		output_tokens: number;
		/** At the model provider's published price. */
		billed_micros: number;
	}[];
}

export interface OnboardingState {
	org_id: string;
	locale: "en" | "zh";
	company_size: string | null;
	is_agency: boolean;
	region: string | null;
	onboarded_at: string | null;
	/** On a plan that includes measurement. The wizard never measures; the workspace does. */
	paid: boolean;
	/** The brand the wizard is holding: the one asked for, or the org's latest. */
	entity: {
		id: string;
		name: string;
		logo_url: string | null;
		description: string | null;
		website: string | null;
		/** Public pages about a brand with no website, read for its facts. */
		source_pages: string[];
		/** Slugs from the category tree, in the order chosen. */
		categories: string[];
		selected_topics: string[];
		/** Questions written per chosen topic, 1–10. */
		per_topic: number;
		/** The engines it chose; empty until the engines screen. */
		engines: string[];
	} | null;
	question_count: number;
}

export interface BrandDraft {
	name: string;
	description: string;
	/** Slugs the homepage read found, at most five. */
	categories: string[];
	aliases: string[];
}

export interface AnalyzeResult {
	domain?: string;
	unreachable?: boolean;
	draft?: BrandDraft;
	page?: { title: string | null; description: string | null };
	/**
	 * A listing already on this domain. Unclaimed, it is the thing to take
	 * rather than duplicate and nothing else comes back; claimed, it is context
	 * beside a draft that is built anyway — one company may run two brands on
	 * one site, and a domain changes hands.
	 */
	existing?: { slug: string; name: string; claimed: boolean } | null;
}

/** One row of an industry's fact schema. */
export interface DatapointField {
	type: "string" | "number" | "boolean" | "enum" | "array";
	label_en: string;
	label_zh: string;
	unit?: string;
	unit_zh?: string;
	/** Where to look this one up — "the vendor's pricing page". */
	source_hint?: string;
	source_hint_zh?: string;
	/** For `enum`, the values it may take. */
	values?: string[];
}

export interface Settings {
	entity: {
		logo_url?: string | null;
		id: string;
		slug: string;
		name: string;
		aliases: string[];
		website: string | null;
		/** Public pages fact collection reads beside the website. */
		source_pages: string[];
		domains: string[];
		/** Domains this brand does not want counted as a rival. */
		excluded_domains: string[];
		/** Rivals the owner named. Pinned onto the competitor axis and the competitors list, never a replacement for what was cited. */
		tracked_domains: string[];
		tagline_en: string | null;
		tagline_zh: string | null;
		/** Keyed by datapoint name; the server refuses one without a source_url. */
		datapoints: Record<
			string,
			{ value?: string | number | boolean | null; unit?: string | null; source_url?: string | null }
		>;
		/**
		 * The facts this brand's industry defines. The server refuses a key that
		 * is not in here, so this is the whole list of facts that can be filled
		 * in — and the empty ones are the reason a brand has no page yet.
		 */
		datapoint_schema: Record<string, DatapointField>;
		/** Slugs from the category tree, in the order chosen. */
		categories: string[];
		/** This brand's tracking switch. The account-wide one is on the run panel. */
		paused: boolean;
		/** What the brand chose; empty when it never did. */
		engines: string[];
		/** What it is measured on: the choice cut to the plan, or the plan's default. */
		engines_effective: string[];
	};
	org: {
		paused: boolean;
		/** How many engines the plan covers per brand. */
		engines_max: number;
		/** Null follows the platform default, which is what `research_model_default` names. */
		research_model: string | null;
		research_model_default: string;
	} | null;
}

/** One day's fetches, split by why the crawlers came. */
export interface CrawlDay {
	date: string;
	training: number;
	search: number;
	live: number;
	preview: number;
}

/** One crawler's traffic in the window. */
export interface CrawlerBot {
	bot: string;
	label: string;
	vendor: string;
	/** training · search · live · preview — why it came, which is the whole point. */
	purpose: string;
	hits: number;
	errors: number;
	pages: number;
}

export interface Crawlers {
	/** True once anything has ever been counted. Distinct from "nothing in this window". */
	ever: boolean;
	/** True once a brand's own domain has reported, i.e. the edge worker is live. */
	own_site: boolean;
	total_hits: number;
	total_errors: number;
	bots: CrawlerBot[];
	series: CrawlDay[];
	/** `page_id` is set where the path is one of this brand's own published
	 *  pages — the seam between "an engine came to read this" and "an engine
	 *  cited this". */
	top_pages: { host: string; path: string; page_id: string | null; hits: number; errors: number }[];
	/** The secret for the brand's own edge worker, once one has been minted. */
	token: string | null;
	site_host: string;
}

// ---------------------------------------------------------------------------
// Referral traffic — stage five
// ---------------------------------------------------------------------------

/** Which of the two things measured a stretch of days. */
export type TrafficSource = "site" | "ga4";

export interface TrafficSegment {
	source: TrafficSource;
	from: string;
	to: string;
}

/**
 * The frame every traffic reading carries.
 *
 * `available` is false where a reading needs individual events and the segment
 * was measured by GA — GA's Data API hands back aggregates only. The screen
 * says so rather than drawing an empty chart, because no bars reads as "no
 * traffic", which is a different and wrong answer.
 */
export interface TrafficFrame {
	authorization_required: boolean;
	setup_required: boolean;
	source: TrafficSource | null;
	segment: TrafficSegment | null;
	/** Older stretches of the same range, measured by the other source. */
	earlier: TrafficSegment[];
	available: boolean;
}

/** GA4's words, because the customer already reads them on GA's own screens. */
export interface TrafficTotals {
	sessions: number;
	/** Null on a GA-measured range: GA stores a distinct count per dimension
	 *  combination and such counts cannot be added, so there is no honest
	 *  number to show. */
	active_users: number | null;
	engaged_sessions: number;
	page_views: number;
	conversions: number;
	/** Seconds per session, or null when nothing was measured. */
	avg_engagement_time: number | null;
	engagement_rate: number | null;
	bounce_rate: number | null;
}

export interface TrafficSlice {
	key: string;
	sessions: number;
	active_users: number | null;
	engaged_sessions: number;
	page_views: number;
	conversions: number;
	engagement_rate: number | null;
}

export interface TrafficPoint {
	day: string;
	sessions: number;
}

export interface TrafficOverview extends TrafficFrame {
	/** AI referrals only. */
	totals: TrafficTotals;
	/** The window before, or null when a different source measured it. */
	before: TrafficTotals | null;
	/** Every referral, so the AI share has a denominator. */
	all: TrafficTotals;
	series: TrafficPoint[];
	engines: TrafficSlice[];
	landing: TrafficSlice[];
	countries: TrafficSlice[];
	devices: TrafficSlice[];
	referrers: TrafficSlice[];
	campaigns: TrafficSlice[];
}

export interface TrafficCredit {
	engine: string;
	conversions: number;
	sessions: number;
}

export interface TrafficAttribution extends TrafficFrame {
	model: "first" | "last";
	credits: TrafficCredit[];
}

export interface TrafficStep {
	from: string;
	/** Null where the visit ended on that page. */
	to: string | null;
	sessions: number;
}

export interface TrafficJourneys extends TrafficFrame {
	steps: TrafficStep[];
}

export interface TrafficVitalsRow {
	path: string;
	views: number;
	/** The 75th percentile, which is how Core Web Vitals are defined. */
	lcp: number | null;
	inp: number | null;
	cls: number | null;
	fcp: number | null;
	ttfb: number | null;
}

export interface TrafficVitals extends TrafficFrame {
	pages: TrafficVitalsRow[];
}

export interface TrafficHit {
	at: string;
	engine: string | null;
	path: string;
	country: string | null;
	device: string | null;
}

export interface TrafficLive extends TrafficFrame {
	hits: TrafficHit[];
}

export interface TrafficGoal {
	id: string;
	name: string;
	kind: "path" | "event";
	value: string;
	conversions: number;
}

export interface TrafficGoals extends TrafficFrame {
	goals: TrafficGoal[];
}

export interface TrafficSetup {
	source: TrafficSource | null;
	/** Absolute, from the server. The snippet is pasted onto someone else's
	 *  domain, where a relative path would load from theirs. */
	script_url: string;
	/** Null until the brand asks for one. */
	token: string | null;
	cookie: boolean;
	retention_days: number;
	ga_property_id: string | null;
	ga_timezone: string | null;
	ga_last_synced_at: string | null;
	/** The address the customer adds as a Viewer in their own GA admin. Null
	 *  when this deployment has no service account configured. */
	ga_credential_id: string | null;
	ga_oauth_configured: boolean;
	ga_authorized: boolean;
	/** The brand's connected Google Analytics accounts. */
	ga_accounts: { id: string; name: string }[];
}

export interface Task {
	sampling_prompts?: { id: string; text: string }[] | null;
	trigger?: string | null;
	id: string;
	kind: string;
	status: "queued" | "running" | "done" | "failed" | "skipped";
	entity_id: string | null;
	brand: string | null;
	title: string | null;
	recipe: string | null;
	locale: string | null;
	engine: string | null;
	batch_id: string | null;
	page_id: string | null;
	created_at: string;
	run_after: string;
	started_at: string | null;
	finished_at: string | null;
	updated_at: string;
	attempts: number;
	max_attempts: number;
	/**
	 * When somebody stopped it. Not a status: a paused task is still queued —
	 * or still running, for the moment between the request and the worker
	 * noticing — and what the mark changes is that nothing claims it.
	 */
	paused_at: string | null;
	/** What a running task last reported of itself; null until it reports, and once it ends. */
	progress: { done: number; total: number; note: string; at: string } | null;
	gate_reason: string | null;
	error_code: string | null;
	error_detail: string | null;
}

export interface TaskList {
	rows: Task[];
	total: number;
	page: number;
	page_size: number;
	counts: Record<Task["status"], number>;
}
interface TaskDetail {
	task: Task;
	events: {
		at: string;
		level: string;
		code: string;
		detail: { attempt?: number; code?: string; public_detail?: string } | null;
	}[];
}
export interface TaskFilters {
	entity?: string;
	kind?: string;
	status?: string;
	search?: string;
	days?: number;
	page?: number;
}

export interface GenerationJob {
	id: string;
	title: string | null;
	reason: string | null;
	created_at: string;
	page_id: string | null;
	recipe: string;
	locale: string;
	status: "queued" | "running" | "done" | "failed" | "skipped";
	target: string | null;
	updated_at: string;
}

/** One model an org may run its analysis on, priced at what it would be billed. */
export interface ResearchModel {
	model: string;
	display_name: string;
	provider: string;
	/** USD per million tokens, at list price — what this org pays, not what ondit does. */
	input_per_m: number;
	output_per_m: number;
	context_length: number;
}

interface ClaimChallenge {
	id: string;
	entity_id: string;
	entity_name: string;
	slug: string;
	domain: string;
	token: string;
	/** Rendered by the server so the string a customer copies is the one it checks. */
	dns_record_name: string;
	dns_record_value: string;
	meta_tag: string;
	status: string;
	attempts: number;
	last_error: string | null;
}

export interface ClaimResult {
	status: string;
	method: string | null;
	/** A message key, never prose. */
	reason: string | null;
	attempts: number;
}

// -- shapes the server owns --------------------------------------------------

/**
 * A person in the account, and the brands they hold.
 *
 * Two roles, at two levels. `role` is what they may do to the account — pay
 * for it, decide who else is in it. `brands` is what they may do to each brand,
 * and it is empty for an owner or an admin: `all_brands` says they hold every
 * one without a row saying so, including the brand added next week.
 */
export interface BrandGrant {
	entity_id: string;
	role: "editor" | "viewer";
}

export interface Member {
	user_id: string;
	email: string;
	name: string | null;
	role: "owner" | "admin" | "member";
	joined_at: string;
	last_seen_at: string | null;
	brands: BrandGrant[];
	all_brands: boolean;
}

/** An invitation nobody has answered. It holds a seat while it is open. */
export interface PendingInvite {
	id: string;
	email: string;
	role: "admin" | "member";
	brands: BrandGrant[];
	invited_by: string | null;
	created_at: string;
	expires_at: string;
}

export interface Seats {
	members: number;
	pending: number;
	/** Zero is enterprise: a number the contract sets, not the plan table. */
	max: number;
}

export interface People {
	/** The reader's own account role. */
	role: "owner" | "admin" | "member";
	may_manage: boolean;
	seats: Seats;
	members: Member[];
	/** Empty for somebody who may not manage: a held seat is theirs to know
	 *  about only if they can do something about it. */
	invites: PendingInvite[];
	brands: { id: string; name: string }[];
}

/** What an invitation link says before anybody signs in. */
export interface InvitePreview {
	org_name: string;
	/** The address it was sent to — the one that has to be signed in as. */
	email: string;
	role: string;
	invited_by: string | null;
	expires_at: string;
}

export interface Me {
	user: { id: string; email: string; name: string | null; locale: string; is_admin: boolean };
	orgs: Org[];
}

export interface Org {
	id: string;
	name: string;
	slug: string;
	kind: string;
	plan_key: string;
	paused: boolean;
	/** Null until the wizard finishes; the shell sends a null back to it. */
	onboarded_at: string | null;
	region: string | null;
	role: string;
	/** When this person joined this account, not when the account was made. */
	joined_at: string;
	credits: number;
	/** The part of the balance that lapses with the period, and when. */
	credits_expiring: number;
	credits_expires_at: string | null;
	/** Bought credits, which never lapse. */
	credits_permanent: number;
	entity_count: number;
	/** How many brands the plan covers. Zero is enterprise: no number. */
	entities_max: number;
	/** How many engines a brand may be measured on. */
	engines_max: number;
	/** How many questions a brand may track. Zero is enterprise: no number. */
	questions_max: number;
	/** How many pages the account may generate this month. Zero: no number. */
	pages_per_month: number;
	/**
	 * The two halves of the product a plan either has or does not. The shell
	 * draws neither when the plan lacks them: a mode switch that refuses and a
	 * sidebar row that 402s are both worse than the thing not being there.
	 */
	agent: boolean;
	advanced_insights: boolean;
	/**
	 * The account is on the trial month, which does not renew. What is left of
	 * it is `subscription_ends_at`.
	 */
	trial: boolean;
	/** `none` never subscribed; `expired` had a plan and lost it, and is read-only. */
	subscription_state: "none" | "active" | "expired";
	subscription_status: string | null;
	subscription_ends_at: string | null;
}

export interface OrgEntity {
	id: string;
	slug: string;
	name: string;
	logo_url: string | null;
	status: string;
	datapoint_count: number;
	cited_this_week: number;
	/** What the reader may do to this brand. A viewer reads it and nothing more. */
	role: "editor" | "viewer";
	/**
	 * What this brand is measured on: its choice, cut to what the plan covers.
	 * Every filter bar offers these and no others — an engine the brand does not
	 * run is an engine whose only effect is to empty the screen.
	 */
	engines: string[];
	/**
	 * The languages this brand has questions in. The reading language is
	 * remembered per person, so a brand that was never asked anything in it has
	 * to be read in one it was.
	 */
	locales: string[];
}

export interface Movement {
	before: number;
	after: number;
	delta: number;
	significant: boolean;
}

export interface Overview {
	cited_questions: number;
	tracked_questions: number;
	/** Null when nothing was measured: a zero here is a measurement. */
	share: number | null;
	/** Absent when the samples cannot support a conclusion. */
	movement: Movement | null;
	ondit_pages_cited: number;
	own_site_cited: number;
	planned: { kind: string; label: string; at: string | null }[];
	trend: { week: string; share: number }[];
	competitors: CompetitorRow[];
	last_sampled_at: string | null;
	loop_state: LoopState;
}

/**
 * Where this brand is around the loop, one count per stage.
 *
 * Counts and not ratios — there is no denominator here to get wrong — and
 * `concluded` is reports that reached a conclusion rather than reports that
 * exist, because "no conclusion yet" is an honest answer and not a result.
 */
export interface LoopState {
	answers: number;
	open_gaps: number;
	waiting: number;
	published: number;
	concluded: number;
}

/**
 * One thing worth doing, as the server computed it.
 *
 * The server sends a code and a size; the panel owns every word and every
 * destination. Same rule as the errors: the sentence has to read naturally in
 * two languages, and only the client knows which one is on screen.
 */
export interface NextAction {
	code: string;
	severity: "attention" | "opportunity";
	/** 1 detect, 2 analyse, 3 generate, 4 publish, 5 reflow. */
	stage: number;
	count: number;
	/** The workflow a to-do about the brand's own opening or weekly workflow is about. */
	workflow_id?: string;
}

export interface CompetitorRow {
	domain: string;
	entity_id: string | null;
	name: string | null;
	share: number;
	cited_questions: number;
	/** Named by the owner, so it is on the list whatever the week did. */
	watched?: boolean;
}

/**
 * The competitors screen, in two lists.
 *
 * `rivals` are products — every domain the directory resolved to a listing,
 * plus the ones the owner named. `sources` are the pages that won the citation
 * without being a competitor: Reddit, a review site, somebody's docs. Different
 * findings, different actions, so they stopped being one table.
 */
export interface Competitors {
	rivals: CompetitorRow[];
	sources: CompetitorRow[];
	/** Brands the answers named and linked that are on neither list yet. */
	suggested: { domain: string; names: string[]; mentions: number }[];
}

export interface QuestionRow {
	id: string;
	text: string;
	intent: string;
	tags: string[];
	enabled: boolean;
	scope: "industry" | "private";
	last_sampled_at: string | null;
	cited: boolean;
}

export interface CitationGroup {
	key: string;
	title: string | null;
	count: number;
	is_ondit: boolean;
	is_ours: boolean;
	/** yours · ondit · directory · community · other. */
	category: string;
}

export interface Perception {
	/** Answers in the window, as the denominator for `named`. */
	answers: number;
	named: number;
	named_first: number;
	named_top3: number;
	avg_position: number | null;
	sentiment: { kind: string; n: number }[];
	themes: { theme: string; n: number }[];
	alongside: { entity_id: string; name: string; together: number; ahead_of_you: number }[];
	quotes: {
		/** The answer it was said in, so the quote can be read in context. */
		sample_id: string;
		engine: string;
		question: string;
		descriptor: string;
		sentiment: string | null;
		position: number;
	}[];
	/** Open claims the brand's own datapoints contradict. */
	contradictions: number;
}

/** What a gap is doing, derived from its articles, jobs and reflows. */
export type GapState = "dismissed" | "writing" | "in_review" | "covered" | "improved" | "not_improved" | "open";

/** The tree's filters. Membership is the server's: one definition for the
 *  tree, the overview's loop strip and the to-do list. */
export type ContentFilter = "all" | "pending" | "drafts" | "published" | "dismissed";

export interface ContentArticle {
	id: string;
	kind: string;
	slug: string;
	locale: string;
	title: string;
	/** `review` when a revision is waiting, whatever the live row says. */
	status: string;
	live: boolean;
	updated_at: string;
	published_at: string | null;
	/** The latest reflow's conclusion; null until one is significant. */
	conclusion: "improved" | "not_improved" | null;
	/** Citations in the window and engine the bar names. */
	cited: number;
	/** Crawler fetches of the page's path in the same window. */
	fetched: number;
	url: string;
}

export interface ContentGap {
	id: string;
	/** The brand's default gap: the shelf for pages written for no question. */
	default: boolean;
	question_id: string | null;
	question: string | null;
	topic: string | null;
	locale: string | null;
	/** The recipe written when nobody picks one. */
	recipe: string;
	state: GapState;
	/** Generations queued or running for it. */
	writing: number;
	created_at: string;
	filters: Exclude<ContentFilter, "all">[];
	signals: { engines: string[]; rivals: string[]; latest_week: string | null; weeks: number };
	articles: ContentArticle[];
}

export interface ContentCounts {
	gaps: number;
	pending: number;
	drafts: number;
	published: number;
	dismissed: number;
}

/** What an article for a gap would be written from, before anything is spent. */
export interface GapBrief {
	gap_id: string;
	question: string | null;
	locale: string;
	recipe: string;
	facts: { key: string; value: string; source_url: string | null }[];
	sources: string[];
	blocked: Refusal | null;
}

export interface GapChoice {
	id: string;
	question: string | null;
	default: boolean;
	status: string;
}

/**
 * The closed set of content blocks.
 *
 * A page is a list of these and nothing else — the renderer, the JSON-LD
 * builder and the fact-checker all have to agree about what a page contains,
 * so the editor works in the same vocabulary rather than in HTML.
 */
export type Block =
	| { type: "answer"; text: string }
	| { type: "section"; heading: string; md: string }
	| { type: "faq"; items: { q: string; a: string }[] }
	| { type: "table"; caption?: string | null; columns: string[]; rows: string[][] }
	| { type: "sources"; items: { label: string; url: string }[] }
	| { type: "stat"; label: string; value: string; unit?: string | null; source_url?: string | null }
	| {
			type: "chart";
			title: string;
			kind: "line" | "bar";
			series: { name: string; points: { x: string; y: number }[] }[];
	  }
	| { type: "image"; file_id: string; alt: string; caption?: string | null }
	| { type: "video"; file_id: string; caption?: string | null; transcript?: string | null }
	| { type: "audio"; file_id: string; caption?: string | null; transcript?: string | null };

/** One platform's version of a page revision, and the task it became. */
export interface PageVariant {
	id: string;
	page_id: string;
	page_revision: number;
	revision: number;
	platform: string;
	/** The kind of post on the platform; "" where it takes one. */
	format: string;
	issues: string[];
	approved_at: string | null;
	task_id: string | null;
	state: string | null;
	result_url: string | null;
	document: {
		fields: Record<string, string>;
		media: { file_id: string; alt: string; role: "image" | "video" }[];
		target_url: string;
	};
}

export interface PageDetail {
	/** The gap the article is filed under. */
	gap: { id: string; entity_id: string; question: string | null; default: boolean };
	/** Every gap it could be moved to: same brand, same language. */
	gap_choices: GapChoice[];
	page: {
		kind: string;
		slug: string;
		locale: string;
		revision: number;
		title: string;
		description: string;
		canonical: string;
		blocks: Block[];
		updated_at: string;
		published_at: string | null;
		cited_this_week: number;
	};
	status: string;
	has_live_version: boolean;
	revision_job: { id: string; status: string; error: string | null } | null;
	/** Null when a person wrote the page rather than the pipeline. */
	recipe: string | null;
	entity_ids: string[];
	/** The table the page is allowed to say things from. */
	facts: {
		entity: string;
		key: string;
		label_en?: string | null;
		label_zh?: string | null;
		value: unknown;
		unit: string | null;
		source_url: string | null;
	}[];
	revisions: { revision: number; created_at: string; note: string | null }[];
	reflow: {
		before: number | null;
		after: number | null;
		delta: number;
		significant: boolean;
		created_at: string;
	} | null;
}

/** One word to write, and the word never to write instead of it. */
export interface VoiceTerm {
	use_term: string;
	instead_of: string;
}

export interface BrandVoice {
	voice: string;
	terms: VoiceTerm[];
	/** The pairs every page is already held to, whatever the brand says. */
	house: VoiceTerm[];
}

/** The page again, as a fragment for the brand's own site. */
export interface PageEmbed {
	html: string;
	/** Empty when the page has no FAQ block. */
	jsonld: string;
	canonical: string;
	published: boolean;
}

export interface RecipeCard {
	key: string;
	page_kind: string;
	/** Whether a stronger tier is worth offering for this recipe. */
	deep: boolean;
	needs_target: boolean;
	/** Why it cannot run as things stand. Null means ready. */
	blocked: Refusal | null;
}

export interface TargetCard {
	entity_id: string;
	name: string;
	slug: string;
	/** Facts the two brands have in common. Under three, no comparison. */
	shared: number;
	blocked: Refusal | null;
}

/**
 * One move, and the brief that goes with it.
 *
 * `your_pages` and `rival_pages` are attached by the server from the citation
 * record, never written by the model — a URL a model produced is a URL that may
 * not exist.
 */
export interface Opportunity {
	/** listing · owned · outreach · community. */
	category: string;
	title: string;
	why: string;
	format: string;
	must_cover: string[];
	proof_needed: string | null;
	questions: string[];
	your_pages: CitedPage[];
	rival_pages: CitedPage[];
}

export interface CitedPage {
	url: string;
	title: string | null;
	domain: string;
	ours: boolean;
}

interface OpportunitiesReport {
	summary: string[];
	opportunities: Opportunity[];
	risks: string[];
}

export interface BrandRun {
	entity_id: string;
	name: string;
	paused: boolean;
	running: number;
	queued: number;
	answers_this_week: number;
	questions: number;
	last_answer_at: string | null;
	spent_24h_micros: number;
	/** What the reader may do to this brand. Per row: a member can edit one
	 *  brand in the account and only read the next. */
	role: "editor" | "viewer";
}

export interface Runs {
	paused: boolean;
	cap_micros: number;
	spent_24h_micros: number;
	spent_month_micros: number;
	week: string;
	brands: BrandRun[];
	/** Work a gate refused, with the reason. A skip is not a failure and never alerts. */
	skipped: { kind: string; reason: string | null; at: string }[];
}

export interface Opportunities {
	/** The stored report's id; null when there is none. */
	id: string | null;
	report: OpportunitiesReport | null;
	model: string | null;
	generated_at: string | null;
	/** True when there is no report, or the one there is has aged out. */
	stale: boolean;
	/** Too few answers in the window to advise on; null when a report can be written. */
	blocked: { answers: number; needed: number } | null;
	balance: number;
	window_days: number;
}

export interface PageSummary {
	id: string;
	kind: string;
	slug: string;
	locale: string;
	title: string;
	status: string;
	has_live_version: boolean;
	revision: number;
	published_at: string | null;
	updated_at: string;
	/** Citations inside the window and engine the scope bar names. */
	cited: number;
	/** Crawler fetches of this page's own path, in the same window. The step
	 *  before the citation: a page no engine has fetched is not a page that
	 *  failed to be cited, it is a page nobody read. */
	crawled: number;
	/** The gap this page is filed under; no question for the default gap. */
	gap_id: string;
	gap_question: string | null;
}

export interface ClaimRow {
	id: string;
	/** The answer the claim was read out of. */
	sample_id: string;
	claim: string;
	datapoint_key: string | null;
	truth: string;
	severity: string;
	status: string;
	created_at: string;
	engine: string;
	question: string;
}

/**
 * A published page and what has happened since it went live.
 *
 * The two counts are counts. Whether the difference means anything is the
 * reflow report's answer, and `significant` is null until there is one.
 */
export interface PageStanding {
	page_id: string;
	title: string;
	kind: string;
	published_at: string;
	days_live: number;
	cited_before: number;
	cited_since: number;
	reflow_id: string | null;
	significant: boolean | null;
	delta: number | null;
}

export interface ReflowRow {
	id: string;
	page_id: string;
	page_title: string;
	before_week: string;
	after_week: string;
	before_share: number | null;
	after_share: number | null;
	delta: number | null;
	significant: boolean;
	created_at: string;
}

export interface LedgerEntry {
	id: string;
	delta: number;
	balance_after: number;
	reason: string;
	ref_kind: string | null;
	ref_id: string | null;
	/** The job kind or metered call kind the reference led to, when it led anywhere. */
	detail: string | null;
	/** The brand that work was for. */
	entity: string | null;
	created_at: string;
}

// -- the rows behind a number ------------------------------------------------

/**
 * One thing an answer cited.
 *
 * `ours` is "this citation is of the brand being looked at" and `is_ondit` is
 * "the page it points at is one of ours". Both, neither, or either: a directory
 * page about the brand is both, the brand's own site is the first only.
 */
export interface EvidenceCitation {
	url: string;
	domain: string;
	title: string | null;
	position: number;
	is_ondit: boolean;
	ours: boolean;
}

/** One answer an engine gave, with the opening of it. */
export interface EvidenceAnswer {
	id: string;
	question_id: string;
	question: string;
	engine: string;
	model: string;
	week: string;
	created_at: string;
	cited: boolean;
	/** Where the brand landed among the names in the answer, when it was named. */
	position: number | null;
	sentiment: string | null;
	descriptor: string | null;
	excerpt: string;
	citations: EvidenceCitation[];
}

/** One question, counted the way the overview counts it. */
interface EvidenceQuestion {
	question_id: string;
	question: string;
	intent: string;
	replications: number;
	cited: number;
	/** A majority of the replications on at least one engine. */
	majority: boolean;
	engines: { engine: string; cited: number; replications: number }[];
}

/** One citation record, for the numbers that count citations rather than answers. */
interface EvidenceCitationRow extends EvidenceCitation {
	id: string;
	sample_id: string;
	question_id: string;
	question: string;
	engine: string;
	created_at: string;
}

export interface Evidence {
	group: "question" | "answer" | "citation";
	total: number;
	questions?: EvidenceQuestion[];
	answers?: EvidenceAnswer[];
	citations?: EvidenceCitationRow[];
}

/** Everything the sampler kept about one answer. */
interface AnswerDetail {
	audience?: {
		persona_id?: string;
		persona_name?: string;
		profile?: Record<string, string>;
		observed_country?: string;
		surface?: string;
	};
	id: string;
	question_id: string;
	question: string;
	engine: string;
	model: string;
	week: string;
	created_at: string;
	text: string | null;
	/** A refusal the gateway returned inside a 200. Never counted as an answer. */
	notice: string | null;
	web_queries: string[];
	citations: EvidenceCitation[];
	mentions: {
		entity_id: string;
		name: string;
		position: number;
		sentiment: string | null;
		descriptor: string | null;
		you: boolean;
	}[];
}

/** A rival, opened from their name. */
interface RivalDetail {
	domain: string;
	entity_id: string | null;
	name: string | null;
	slug: string | null;
	week: string;
	share: number | null;
	cited_questions: number;
	trend: { week: string; share: number }[];
	pages: { url: string; title: string | null; count: number }[];
	/** Per question: how many answers cited them, and how many cited you. */
	questions: { question_id: string; question: string; answers: number; theirs: number; yours: number }[];
}

interface CrawlDetail {
	bot: string | null;
	host: string | null;
	path: string | null;
	rows: {
		key: string;
		path: string;
		label: string | null;
		vendor: string | null;
		purpose: string | null;
		hits: number;
		errors: number;
	}[];
	series: { date: string; hits: number; errors: number }[];
}

/**
 * What narrows a drill-down.
 *
 * Everything is optional and everything is a string, because this is a query
 * string: the server reads an absent parameter as "every row" and the panel
 * never has to decide what a default means.
 */
export interface EvidenceFilters extends Filters {
	persona?: string;
	region?: string;
	topics?: string;
	intent?: string;
	group?: "question" | "answer" | "citation";
	question?: string;
	/** An ISO week, or `current` for the one the overview's headline counts. */
	week?: string;
	domain?: string;
	url?: string;
	page?: string;
	source?: "ondit" | "own" | "ours";
	cited?: "yes" | "no";
	sentiment?: string;
	theme?: string;
	named?: "yes" | "first";
	limit?: number;
}

export interface Filters {
	range?: string;
	engine?: string;
	locale?: string;
}

/**
 * A row of the pricing table, as the server stores it.
 *
 * Four prices rather than one: a yearly price is its own product on the
 * processor's side, and the currency a customer pays in is decided by the
 * language they read, so the panel never divides or converts a price itself.
 */
interface PlanSummary {
	key: string;
	name_en: string;
	name_zh: string;
	monthly_credits: number;
	price_usd_micros: number;
	price_cny_micros: number;
}

/** The whole row. `current` is the summary: the org's own plan is read off the
 *  org, and the columns a chooser needs come with the catalogue. */
export interface Plan extends PlanSummary {
	price_usd_yearly_micros: number;
	price_cny_yearly_micros: number;
	cname: boolean;
	engines_max: number;
	seats_max: number;
	/** Zero on the enterprise row, where the contract sets the number. */
	entities_max: number;
	/** What the plan includes beyond its numbers. */
	agent: boolean;
	workflows_max: number;
	advanced_insights: boolean;
	/** One month, once per account, never renewed. */
	trial: boolean;
	questions_max: number;
	pages_per_month: number;
	/** Enterprise: arranged by contract, so there is nothing to click. */
	contact_only: boolean;
}

export interface Subscription {
	plan_key: string;
	status: string;
	current_period_end: string | null;
	cancel_at_period_end: boolean;
	/** A downgrade waiting for the period to end. */
	pending_plan_key: string | null;
	interval: "month" | "year";
}

/** One way the account is bigger than the plan it wants to move down to. */
export interface PlanOverflow {
	what: "brands" | "seats" | "questions" | "engines";
	have: number;
	max: number;
	entity_id: string | null;
	entity_name: string | null;
}

export interface PlanPreview {
	plan_key: string;
	fits: boolean;
	over: PlanOverflow[];
	effective_at: string | null;
}

/** A credit pack for sale. Prices in micros, like everything else. */
export interface CreditPack {
	key: string;
	credits: number;
	price_usd_micros: number;
	price_cny_micros: number;
	price_id_usd: string | null;
	price_id_cny: string | null;
}

/** The balance, split by lifetime. */
export interface CreditBreakdown {
	balance: number;
	permanent: number;
	expiring: number;
	expires_at: string | null;
}

export interface Billing {
	/** Null on an org whose `plan_key` no longer names a row in `plans`. */
	current: PlanSummary | null;
	subscription: Subscription | null;
	subscription_state: "none" | "active" | "expired";
	/** The trial month has been taken — live or lapsed. It is not offered twice. */
	trial_used: boolean;
	plans: Plan[];
	balance: number;
	credits: CreditBreakdown;
	packs: CreditPack[];
	credits_per_usd: number;
}

// ---------------------------------------------------------------------------
// Answer engine insights
// ---------------------------------------------------------------------------

/**
 * The five metrics, by the names the server uses.
 *
 * A union rather than strings: every block on every insight tab is keyed by one
 * of these, and the label, the unit and the direction all hang off it.
 */
export type MetricName = "visibility" | "share_of_voice" | "average_position" | "citation_share" | "sentiment";

export const METRICS: readonly MetricName[] = [
	"visibility",
	"share_of_voice",
	"average_position",
	"citation_share",
	"sentiment",
];

/** Position is a rank, so smaller is better and the arrow points the other way. */
export function lowerIsBetter(metric: MetricName): boolean {
	return metric === "average_position";
}

/** Everything but position is a percentage of the same window. */
export function isPercent(metric: MetricName): boolean {
	return metric !== "average_position";
}

/** A brand a number can be about: this one, or one it shares answers with. */
interface InsightAsset {
	entity_id: string;
	name: string;
	domain: string | null;
	logo_url: string | null;
	is_you: boolean;
}

export interface AssetValue {
	entity_id: string;
	name: string;
	domain: string | null;
	is_you: boolean;
	/** Null is "not measured" and prints as an em dash. Never a zero. */
	value: number | null;
	previous: number | null;
	rank: number | null;
}

export interface SeriesPoint {
	/** A day, a topic, an engine or a prompt id, depending on the axis. */
	bucket: string;
	entity_id: string;
	value: number | null;
}

/** One metric block: the figure, where it was, where it ranks, and the series. */
export interface MetricBlock {
	metric: MetricName;
	value: number | null;
	previous: number | null;
	rank: number | null;
	/** How many brands were ranked, so "3rd" can be printed as "3rd of 11". */
	ranked: number;
	/**
	 * The answers behind the figure and behind the one before it. A movement
	 * is only reported when both are enough to support one; absent on a block
	 * the server has not counted yet.
	 */
	samples?: number;
	samples_previous?: number;
	assets: AssetValue[];
	series: SeriesPoint[];
}

export interface TopicRow {
	/** The empty string is the real bucket for prompts nobody filed. */
	topic: string;
	prompts: number;
	samples: number;
	visibility: number | null;
	citation_share: number | null;
	rank: number | null;
	ranked: number;
	assets: AssetValue[];
}

export interface VisibilityTab {
	assets: InsightAsset[];
	samples: number;
	samples_previous: number;
	visibility: MetricBlock;
	share_of_voice: MetricBlock;
	average_position: MetricBlock;
	topics: TopicRow[];
}

export interface PromptRow {
	id: string;
	text: string;
	topic: string;
	intent: string;
	scope: string;
	runs: number;
	visibility: number | null;
	share_of_voice: number | null;
	average_position: number | null;
	citation_share: number | null;
	sentiment: number | null;
	visibility_rank: number | null;
	citation_rank: number | null;
	ranked: number;
	last_sampled_at: string | null;
}

export interface PromptsTab {
	prompts: PromptRow[];
	used: number;
	topics: string[];
}

export interface FanoutRow {
	id: string;
	text: string;
	topic: string;
	queries: number;
	runs: number;
	per_run: number | null;
	/** Answers that ran at least one search; `rate` is that over `runs`. */
	searched: number;
	rate: number | null;
	examples: string[];
}

export interface FanoutsTab {
	prompts: FanoutRow[];
	platforms: { engine: string; queries: number; runs: number; per_run: number | null; rank: number | null }[];
	/** Prompts that reported any search at all. Coverage is uneven by platform. */
	reported: number;
	runs: number;
}

export interface PlatformValue {
	engine: string;
	samples: number;
	value: number | null;
	previous: number | null;
	rank: number | null;
}

export interface InsightAudience {
	id: string;
	entity_id: string;
	kind: "region" | "persona";
	name: string;
	profile: Record<string, string>;
	topics: string[];
	question_ids: string[];
	archived: boolean;
}
export interface AudienceReport {
	countries: string[];
	dimension: "regions" | "personas" | "platforms";
	definitions: InsightAudience[];
	rows: {
		id: string;
		name: string;
		samples: number;
		previous_samples: number;
		metrics: { metric: MetricName; value: number | null; previous: number | null }[];
		trend: { date: string; samples: number; metrics: Record<MetricName, number | null> }[];
	}[];
	unassigned_samples: number;
	sampling_available: boolean;
	method: string;
}

export interface PlatformsTab {
	assets: InsightAsset[];
	platforms: string[];
	metrics: {
		metric: MetricName;
		rows: PlatformValue[];
		matrix: { engine: string; entity_id: string; value: number | null }[];
	}[];
}

export interface NegativeReview {
	sample_id: string;
	question: string;
	engine: string;
	descriptor: string | null;
	themes: string[];
	claims: { id: string; claim: string; truth: string; source_urls: string[] }[];
}

export interface SentimentTab {
	assets: InsightAsset[];
	block: MetricBlock;
	/** Mentions the reading pass has looked at, over all of them. */
	read: number;
	mentions: number;
	moods: { kind: string; n: number }[];
	themes: { theme: string; n: number }[];
	negative_reviews: NegativeReview[];
	quotes: {
		sample_id: string;
		question: string;
		engine: string;
		position: number;
		sentiment: string | null;
		descriptor: string;
	}[];
}

/** The seven categories a cited URL can be filed under. */
export const CITATION_CATEGORIES = [
	"yours",
	"ondit",
	"competitor",
	"directory",
	"community",
	"editorial",
	"other",
] as const;
type CitationCategory = (typeof CITATION_CATEGORIES)[number];

export interface CitedRow {
	/** The domain, or the full URL on a page row. */
	key: string;
	domain: string;
	title: string | null;
	category: CitationCategory;
	tags: string[];
	count: number;
	share: number;
	rank: number;
	is_ours: boolean;
	is_ondit: boolean;
	/** Answers that cited this *and* named the brand. */
	with_mention: number;
	first_cited_at: string | null;
}

export interface CitationsTab {
	assets: InsightAsset[];
	citation_share: MetricBlock;
	mix: { category: CitationCategory; count: number; share: number }[];
	domains: CitedRow[];
	pages: CitedRow[];
	monitored: CitedRow[];
	tags: string[];
}

interface CitationRule {
	id: string;
	url: string;
	category: CitationCategory;
	subpaths: boolean;
	created_at: string;
}

interface CitationTag {
	id: string;
	url: string;
	tags: string[];
	subpaths: boolean;
	created_at: string;
}

interface MonitoredPage {
	id: string;
	url: string;
	note: string | null;
	first_cited_at: string | null;
	created_at: string;
}

export interface NameMatching {
	mapped: { name: string; mentions: number; primary: boolean }[];
	/** Names the answers used that resolved to nothing. Visibility not credited. */
	unmapped: { name: string; domain: string | null; mentions: number; last_seen_at: string }[];
	aliases: string[];
	resolution_kinds: string[];
	resolution_notes: string | null;
	tracked_domains: string[];
	excluded_domains: string[];
}

export interface SavedView {
	id: string;
	name: string;
	tab: string;
	filters: Record<string, string>;
	created_at: string;
}

/**
 * The insight filter bar.
 *
 * Extends the three global filters rather than replacing them: a person who set
 * the engine on the overview and then opened an insight tab is still looking at
 * that engine, and a second, separate notion of "which engine" is how two
 * screens come to disagree about what they are showing.
 */
interface InsightFilters extends Filters {
	/** Comma-separated; a multi-select that survives every proxy. */
	topics?: string;
	/** One `questions.intent` — what Profound calls a prompt type. */
	intent?: string;
	category?: string;
	tag?: string;
}

export interface ReviewInboxItem {
	id: string;
	kind: "page_review" | "publishing_review" | "publishing_plan" | "distribution_review";
	entity_id: string;
	brand: string;
	title: string;
	created_at: string;
	deadline: string | null;
	page_id: string | null;
	platform: string | null;
}

/** `server/src/notifications` KINDS, in the order Settings lists them. */
export const NOTIFICATION_KINDS = [
	"review",
	"workflow_waiting",
	"workflow_failed",
	"workflow_message",
	"task_failed",
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export interface NotificationItem {
	id: string;
	kind: NotificationKind;
	entity_id: string | null;
	/** Named facts, never the server's prose: `brand`, `subject`, a failed `task`'s kind, and for a workflow's message the author's `title` and `body`. */
	payload: { brand?: string; subject?: string | null; title?: string; body?: string; task?: string };
	/** A path in this application. */
	link: string | null;
	read_at: string | null;
	created_at: string;
}

export interface NotificationSettings {
	kinds: NotificationKind[];
	/** The kinds that go into the letter. */
	mailed: NotificationKind[];
	digest: "hourly" | "daily";
	/** A confirmed address other than the sign-in one; null means the sign-in one. */
	email: string | null;
	sign_in_email: string;
	sign_in_verified: boolean;
}

export const api = {
	reviewInbox: (org: string) => get<{ items: ReviewInboxItem[] }>(`/orgs/${org}/review-inbox`),
	notifications: (org: string) => get<{ items: NotificationItem[]; unread: number }>(`/orgs/${org}/notifications`),
	/** Without ids, everything in the account. */
	readNotifications: (org: string, ids?: string[]) => post<{ ok: boolean }>(`/orgs/${org}/notifications/read`, { ids }),
	notificationSettings: () => get<NotificationSettings>("/me/notifications"),
	saveNotificationSettings: (body: {
		mailed?: NotificationKind[];
		digest?: "hourly" | "daily";
		reset_email?: boolean;
	}) => patch<NotificationSettings>("/me/notifications", body),
	sendNotificationEmailCode: (email: string) => post<{ sent: boolean }>("/me/notifications/email", { email }),
	confirmNotificationEmail: (code: string) => post<NotificationSettings>("/me/notifications/email/confirm", { code }),
	me: () => get<Me>("/me"),
	updateProfile: (name: string) => post<{ name: string }>("/me", { name }),
	/** Close the account: the orgs this person owns, and their seat in the rest. */
	closeAccount: () => del<{ orgs: number; brands: number; left: number }>("/me"),
	entities: (org: string) => get<OrgEntity[]>(`/orgs/${org}/entities`),
	credits: (org: string, cursor?: string) =>
		get<{ balance: number; credits: CreditBreakdown; entries: LedgerEntry[]; next_cursor: string | null }>(
			`/orgs/${org}/credits${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`,
		),
	billing: (org: string) => get<Billing>(`/orgs/${org}/billing`),
	people: (org: string) => get<People>(`/orgs/${org}/members`),
	invite: (org: string, body: { email: string; role: string; brands: BrandGrant[] }) =>
		post<{ id: string; email: string; role: string; sent: boolean }>(`/orgs/${org}/invites`, body),
	resendInvite: (org: string, id: string) => post<{ sent: boolean }>(`/orgs/${org}/invites/${id}/resend`),
	revokeInvite: (org: string, id: string) => del<{ revoked: boolean }>(`/orgs/${org}/invites/${id}`),
	updateMember: (org: string, user: string, body: { role?: string; brands?: BrandGrant[] }) =>
		patch<{ ok: boolean }>(`/orgs/${org}/members/${user}`, body),
	removeMember: (org: string, user: string) => del<{ removed: boolean }>(`/orgs/${org}/members/${user}`),
	// Named by a token rather than an id, and readable before sign-in: the
	// page it fills in is the one that says which address to sign in as.
	invitePreview: (token: string) => get<InvitePreview>(`/invites/${encodeURIComponent(token)}`),
	acceptInvite: (token: string) => post<{ org_id: string }>(`/invites/${encodeURIComponent(token)}/accept`),
	usage: (org: string, range?: string) => get<Usage>(`/orgs/${org}/usage${range ? `?range=${range}` : ""}`),
	register: (email: string, password: string, locale: string) =>
		post<{ verification_required: true }>("/auth/register", {
			email,
			password,
			locale,
		}),
	signIn: (email: string, password: string) => post<{ ok: boolean }>("/auth/sign-in", { email, password }),
	/** Which provider buttons this deployment can honour. Asked before drawing them. */
	authProviders: () => get<{ google: boolean; github: boolean }>("/auth/providers"),
	resendVerification: (email: string) => post<{ sent: boolean }>("/auth/resend", { email }),
	forgotPassword: (email: string) => post<{ sent: boolean }>("/auth/forgot", { email }),
	verifyEmail: (email: string, code: string) => post<{ verified: boolean }>("/auth/verify", { email, code }),
	resetPassword: (token: string, password: string) => post<{ ok: boolean }>("/auth/reset", { token, password }),
	subscribe: (org: string, body: { plan_key: string; interval: "month" | "year"; currency?: string }) =>
		post<{ payment_id: string; client_secret: string | null; checkout_url: string; status: string }>(
			`/orgs/${org}/subscribe`,
			body,
		),
	planPreview: (org: string, planKey: string) =>
		get<PlanPreview>(`/orgs/${org}/plan/preview${query({ plan_key: planKey })}`),
	changePlan: (org: string, planKey: string) =>
		post<{ pending_plan_key: string; effective_at: string | null }>(`/orgs/${org}/plan/change`, { plan_key: planKey }),
	cancelPlanChange: (org: string) => del<{ pending_plan_key: null }>(`/orgs/${org}/plan/change`),
	/** Stop the recurring charge at the end of the period that is paid for. */
	cancelSubscription: (org: string) =>
		post<{ subscription: Subscription; subscription_state: string; effective_at: string | null }>(
			`/orgs/${org}/subscription/cancel`,
			{},
		),
	topup: (org: string, body: { pack_key: string; currency?: string }) =>
		post<{ payment_id: string; client_secret: string; status: string; credits: number }>(`/orgs/${org}/topup`, body),
	deleteEntity: (entity: string) =>
		del<{ deleted: boolean; released: boolean; pages_removed: number }>(`/entities/${entity}`),
	// Every entity-scoped call here names the brand it is working on. The
	// wizard leaves it out on the screen that creates one, and again while the
	// first run is in progress — the server resolves that to the draft it is
	// holding — but the add-a-brand flow always says which.
	//
	// The add-a-brand flow also names the account it is adding to (`org_id`):
	// the person is looking at one workspace and may be in several, and a brand
	// written to whichever account the server picked would land in the wrong
	// one. The first-run wizard has only the one and leaves it out.
	onboarding: {
		state: (entity?: string) => get<OnboardingState>(`/onboarding/state${query({ entity })}`),
		company: (body: { company_size: string | null; is_agency: boolean }) =>
			post<{ ok: boolean }>("/onboarding/company", body),
		analyze: (website: string, org?: string) =>
			post<AnalyzeResult>("/onboarding/analyze", { website, ...(org ? { org_id: org } : {}) }),
		brand: (body: {
			request_id?: string;
			entity_id?: string | null;
			org_id?: string;
			name: string;
			description: string;
			website?: string | null;
			/** At most five. Absent keeps what the brand had. */
			source_pages?: string[];
			/** Slugs, at most five. Absent keeps what the brand had. */
			categories?: string[];
			aliases?: string[];
		}) => post<{ entity_id: string; slug?: string }>("/onboarding/brand", body),
		region: (body: { region: string | null; locale: string }) =>
			post<{ region: string | null; locale: string }>("/onboarding/region", body),
		topics: (entity?: string, org?: string) =>
			post<{ topics: string[] }>(`/onboarding/topics${query({ entity })}`, org ? { org_id: org } : undefined),
		saveTopics: (topics: string[], perTopic: number, entity?: string, org?: string) =>
			post<{ topics: string[]; per_topic: number }>("/onboarding/topics/selection", {
				topics,
				per_topic: perTopic,
				entity_id: entity,
				...(org ? { org_id: org } : {}),
			}),
		finish: (entity?: string, org?: string) =>
			post<{
				entity_id: string;
				/** Null while the post-payment preparation task is pending. */
				handoff: {
					opening: { workflow_id: string; run_id: string };
					loop: { workflow_id: string };
				} | null;
			}>("/onboarding/finish", {
				entity_id: entity ?? null,
				...(org ? { org_id: org } : {}),
			}),
		engines: (engines: string[], entity?: string, org?: string) =>
			post<{ entity_id: string; engines: string[] }>("/onboarding/engines", {
				engines,
				entity_id: entity ?? null,
				...(org ? { org_id: org } : {}),
			}),
	},
	openClaim: (slug: string, orgId: string) => post<ClaimChallenge>("/claims", { slug, org_id: orgId }),
	verifyClaim: (id: string) => post<ClaimResult>(`/claims/${id}/verify`),

	overview: (entity: string, f: Filters) => get<Overview>(`/entities/${entity}/overview${query(f)}`),
	actions: (entity: string, f: Filters) => get<NextAction[]>(`/entities/${entity}/actions${query(f)}`),
	questions: (entity: string, f: Filters) =>
		get<{ questions: QuestionRow[]; private_count: number }>(`/entities/${entity}/questions${query(f)}`),
	addQuestion: (entity: string, body: { text: string; intent?: string; locale?: string }) =>
		post<{ id: string }>(`/entities/${entity}/questions`, body),
	editQuestion: (entity: string, question: string, body: { text: string; intent: string }) =>
		patch<{ id: string }>(`/entities/${entity}/questions/${question}`, body),
	removeQuestion: (entity: string, question: string) =>
		del<{ ok: boolean }>(`/entities/${entity}/questions/${question}`),

	/**
	 * The answer-engine insight tabs.
	 *
	 * Grouped rather than flattened into the object above: nine screens read
	 * one scope, and a call that reaches for `api.insights.*` is a call whose
	 * filters are the insight filters. The older `visibility` and `citations`
	 * above stay because the overview and the evidence drawer still count the
	 * way they always did.
	 */
	saveAudience: (entity: string, input: Omit<InsightAudience, "id" | "entity_id"> & { id?: string }) =>
		post<{ id: string }>(`/entities/${entity}/operations`, {
			capability: "audiences.save",
			version: 1,
			idempotency_key: crypto.randomUUID(),
			input,
		}),
	samplePersona: (entity: string, locale: string, persona: string) =>
		post<{ id: string; status: string; error_code?: string }>(`/entities/${entity}/operations`, {
			capability: "samples.collect",
			version: 1,
			idempotency_key: crypto.randomUUID(),
			input: { locale, persona_id: persona },
		}),
	insights: {
		audiences: (entity: string, kind: "regions" | "personas", f: InsightFilters) =>
			get<AudienceReport>(`/entities/${entity}/insights/${kind}${query(f)}`),
		visibility: (entity: string, f: InsightFilters) =>
			get<VisibilityTab>(`/entities/${entity}/insights/visibility${query(f)}`),
		topics: (entity: string, f: InsightFilters) => get<TopicRow[]>(`/entities/${entity}/insights/topics${query(f)}`),
		prompts: (entity: string, f: InsightFilters) => get<PromptsTab>(`/entities/${entity}/insights/prompts${query(f)}`),
		fanouts: (entity: string, f: InsightFilters) => get<FanoutsTab>(`/entities/${entity}/insights/fanouts${query(f)}`),
		platforms: (entity: string, f: InsightFilters) =>
			get<PlatformsTab>(`/entities/${entity}/insights/platforms${query(f)}`),
		sentiment: (entity: string, f: InsightFilters) =>
			get<SentimentTab>(`/entities/${entity}/insights/sentiment${query(f)}`),
		citations: (entity: string, f: InsightFilters) =>
			get<CitationsTab>(`/entities/${entity}/insights/citations${query(f)}`),
	},
	citationRules: (entity: string) => get<CitationRule[]>(`/entities/${entity}/citation-rules`),
	addCitationRule: (entity: string, body: { url: string; category: string; subpaths?: boolean }) =>
		post<{ url: string }>(`/entities/${entity}/citation-rules`, body),
	removeCitationRule: (entity: string, rule: string) =>
		del<{ ok: boolean }>(`/entities/${entity}/citation-rules/${rule}`),
	citationTags: (entity: string) => get<CitationTag[]>(`/entities/${entity}/citation-tags`),
	addCitationTag: (entity: string, body: { url: string; tags: string[]; subpaths?: boolean }) =>
		post<{ url: string }>(`/entities/${entity}/citation-tags`, body),
	removeCitationTag: (entity: string, rule: string) =>
		del<{ ok: boolean }>(`/entities/${entity}/citation-tags/${rule}`),
	monitoredPages: (entity: string) => get<MonitoredPage[]>(`/entities/${entity}/monitored-pages`),
	addMonitoredPage: (entity: string, body: { url: string; note?: string }) =>
		post<{ url: string }>(`/entities/${entity}/monitored-pages`, body),
	removeMonitoredPage: (entity: string, page: string) =>
		del<{ ok: boolean }>(`/entities/${entity}/monitored-pages/${page}`),
	nameMatching: (entity: string, f: InsightFilters) =>
		get<NameMatching>(`/entities/${entity}/name-matching${query(f)}`),
	// Three actions because an unmapped name has three answers: it is another
	// name for this brand, it is a rival worth tracking, or it is neither.
	mapName: (entity: string, body: { name: string; action: "map" | "track" | "dismiss" }) =>
		post<{ ok: boolean }>(`/entities/${entity}/name-matching/map`, body),
	brandVoice: (entity: string) => get<BrandVoice>(`/entities/${entity}/voice`),
	saveBrandVoice: (entity: string, body: { voice: string; terms: VoiceTerm[] }) =>
		patch<BrandVoice>(`/entities/${entity}/voice`, body),
	saveResolution: (entity: string, body: { kinds: string[]; notes?: string | null }) =>
		post<{ ok: boolean }>(`/entities/${entity}/resolution`, body),
	saveTracked: (entity: string, body: { tracked_domains?: string[]; excluded_domains?: string[] }) =>
		post<{ tracked_domains: string[]; excluded_domains: string[] }>(`/entities/${entity}/tracked`, body),
	views: (entity: string) => get<SavedView[]>(`/entities/${entity}/views`),
	saveView: (entity: string, body: { name: string; tab: string; filters: Record<string, string> }) =>
		post<{ id: string }>(`/entities/${entity}/views`, body),
	removeView: (entity: string, view: string) => del<{ ok: boolean }>(`/entities/${entity}/views/${view}`),
	setTopic: (entity: string, question: string, topic: string) =>
		post<{ topic: string }>(`/entities/${entity}/questions/${question}/topic`, { topic }),
	citations: (entity: string, f: Filters & { by?: string }) =>
		get<CitationGroup[]>(`/entities/${entity}/citations${query(f)}`),
	competitors: (entity: string, f: InsightFilters) => get<Competitors>(`/entities/${entity}/competitors${query(f)}`),
	addGap: (entity: string, text: string, locale: string) =>
		post<{ id: string }>(`/entities/${entity}/gaps`, { text, locale }),
	content: (entity: string, f: InsightFilters) =>
		get<{ counts: ContentCounts; gaps: ContentGap[]; balance: number }>(`/entities/${entity}/content${query(f)}`),
	gapChoices: (entity: string, locale: string) =>
		get<{ gaps: GapChoice[] }>(`/entities/${entity}/gaps${query({ locale })}`),
	gapBrief: (gap: string, ask: { recipe?: string; other_entity_id?: string; locale?: string }) =>
		get<GapBrief>(`/gaps/${gap}/brief${query(ask)}`),
	dismissGap: (gap: string, restore = false) =>
		post<{ updated: number; status: string }>(`/gaps/${gap}/dismiss`, { restore }),
	movePage: (page: string, gap_id: string) => post<{ gap_id: string }>(`/pages/${page}/gap`, { gap_id }),
	opportunities: (entity: string, f: Filters) => get<Opportunities>(`/entities/${entity}/opportunities${query(f)}`),
	generateOpportunities: (entity: string, f: Filters) =>
		post<{ job_id: string }>(`/entities/${entity}/opportunities${query(f)}`),
	researchModels: (entity: string) => get<{ models: ResearchModel[]; default: string }>(`/entities/${entity}/models`),
	traffic: (entity: string, f: Filters) => get<TrafficOverview>(`/entities/${entity}/traffic${query(f)}`),
	trafficAttribution: (entity: string, f: Filters & { model?: string }) =>
		get<TrafficAttribution>(`/entities/${entity}/traffic/attribution${query(f)}`),
	trafficJourneys: (entity: string, f: Filters) =>
		get<TrafficJourneys>(`/entities/${entity}/traffic/journeys${query(f)}`),
	trafficVitals: (entity: string, f: Filters) => get<TrafficVitals>(`/entities/${entity}/traffic/vitals${query(f)}`),
	trafficLive: (entity: string) => get<TrafficLive>(`/entities/${entity}/traffic/live`),
	trafficGoals: (entity: string, f: Filters) => get<TrafficGoals>(`/entities/${entity}/traffic/goals${query(f)}`),
	addTrafficGoal: (entity: string, body: { name: string; kind: string; value: string }) =>
		post<{ id: string }>(`/entities/${entity}/traffic/goals`, body),
	removeTrafficGoal: (entity: string, goal: string) =>
		del<{ ok: boolean }>(`/entities/${entity}/traffic/goals/${goal}`),
	/**
	 * Starts an OAuth grant for one provider into the brand's credentials and
	 * comes back to `returnURL`. Any provider the server's registry holds: the
	 * Integrations page is where a person browses them, not the only place one
	 * can be connected from.
	 */
	connectCredential: (entity: string, providerId: string, returnURL: string) =>
		post<{ url: string }>("/agent/credentials/personal/connect", {
			workspaceId: entity,
			providerId,
			returnURL,
		}),
	trafficSetup: (entity: string) => get<TrafficSetup>(`/entities/${entity}/traffic/setup`),
	saveTrafficSetup: (
		entity: string,
		body: {
			source?: string;
			ga_property_id?: string;
			ga_timezone?: string;
			ga_credential_id?: string;
			cookie?: boolean;
			retention_days?: number;
			rotate_token?: boolean;
		},
	) => post<TrafficSetup>(`/entities/${entity}/traffic/setup`, body),
	crawlers: (entity: string, f: Filters) => get<Crawlers>(`/entities/${entity}/crawlers${query(f)}`),
	rotateCrawlToken: (entity: string) => post<{ token: string }>(`/entities/${entity}/crawl-token`),
	perception: (entity: string, f: Filters) => get<Perception>(`/entities/${entity}/perception${query(f)}`),
	evidence: (entity: string, f: EvidenceFilters) => get<Evidence>(`/entities/${entity}/evidence${query(f)}`),
	answer: (entity: string, sample: string) => get<AnswerDetail>(`/entities/${entity}/answers/${sample}`),
	rival: (entity: string, domain: string, f: Filters) =>
		get<RivalDetail>(`/entities/${entity}/rivals/${encodeURIComponent(domain)}${query(f)}`),
	crawlDetail: (entity: string, f: Filters & { bot?: string; host?: string; path?: string }) =>
		get<CrawlDetail>(`/entities/${entity}/crawl-detail${query(f)}`),
	runs: (org: string) => get<Runs>(`/orgs/${org}/runs`),
	pauseOrg: (org: string, paused: boolean) => post<{ paused: boolean }>(`/orgs/${org}/pause`, { paused }),
	pauseEntity: (entity: string, paused: boolean) => post<{ paused: boolean }>(`/entities/${entity}/pause`, { paused }),
	pages: (entity: string, f: InsightFilters) => get<PageSummary[]>(`/entities/${entity}/pages${query(f)}`),
	cancelPlatformSchedule: (entity: string, id: string, device: string) =>
		post<BrowserTask>(`/entities/${entity}/platform-publishing/${id}/schedule`, {
			action: "cancel",
			device: device || "website",
		}),
	movePlatformSchedule: (entity: string, id: string, at: string, expectedAt: string) =>
		post<BrowserTask>(`/entities/${entity}/platform-publishing/${id}/schedule`, {
			action: "move",
			device: "website",
			at,
			expected_at: expectedAt,
		}),
	platformTasks: (entity: string) =>
		get<{
			tasks: BrowserTask[];
			accounts: { id: string; provider_id: string; name: string; url: string | null }[];
		}>(`/entities/${entity}/platform-publishing`),
	createPlatformTask: (entity: string, request_id: string, payload: PublishPayload) =>
		post<BrowserTask>(`/entities/${entity}/platform-publishing`, { request_id, payload }),
	reportPlatformTask: (entity: string, id: string, state: string, published_url?: string) =>
		patch(`/entities/${entity}/platform-publishing/${id}`, { state, published_url }),
	publishPlatformTask: (entity: string, id: string, credential_id: string) =>
		post<{ state: string; published_url: string | null }>(`/entities/${entity}/platform-publishing/${id}/publish`, {
			credential_id,
		}),
	pageDistribution: (entity: string, page: string) =>
		get<{ rows: PageDistributionRow[]; platforms: PublishingPlatform[] }>(
			`/entities/${entity}/pages/${page}/distribution`,
		),
	distribution: (entity: string) =>
		get<{ platforms: PublishingPlatform[]; posts: DistributionPost[] }>(`/entities/${entity}/distribution`),
	distributionSource: (entity: string, page: string) =>
		get<DistributionSource>(`/entities/${entity}/distribution/source/${page}`),
	distributionChannels: (entity: string) =>
		get<{ platforms: PublishingPlatform[]; channels: DistributionChannel[] }>(
			`/entities/${entity}/distribution/channels`,
		),
	submitListing: (entity: string, platform_id: string, url: string) =>
		post<{ ok: true }>(`/entities/${entity}/distribution/listings`, { platform_id, url }),
	withdrawListing: (entity: string, platform: string) =>
		del<{ ok: true }>(`/entities/${entity}/distribution/listings/${encodeURIComponent(platform)}`),
	addPublishingPlatform: (entity: string, body: PublishingPlatform) =>
		post<PublishingPlatform>(`/entities/${entity}/distribution/platforms`, body),
	planDistribution: (
		entity: string,
		body: { page_id: string; platform_id: string; format?: string; planned_at: string | null },
	) => post<{ id: string }>(`/entities/${entity}/distribution/plan`, body),
	saveDistribution: (
		entity: string,
		body: {
			page_id: string;
			platform_id: string;
			format?: string;
			source_revision: number;
			body: string;
			published_url: string | null;
		},
	) => post<{ id: string }>(`/entities/${entity}/distribution`, body),
	recommendPlatforms: (entity: string, page_id: string) =>
		post<{ recommendations: { platform_id: string; reason: string; cited_answers: number }[] }>(
			`/entities/${entity}/distribution/recommend`,
			{ page_id },
		),
	page: (page: string) => get<PageDetail>(`/pages/${page}`),
	/** A new, empty article a person or the Agent is about to write. */
	createPage: (entity: string, body: { title: string; locale: string; gap_id?: string }) =>
		post<{ page_id: string; revision: number; title: string }>(`/entities/${entity}/pages`, body),
	/** The page's platform versions, for its live revision. */
	pageVariants: (page: string) => get<{ revision: number; variants: PageVariant[] }>(`/pages/${page}/variants`),
	/** Make the live revision's version for each platform. */
	adaptPage: (entity: string, page: string, platforms: string[]) =>
		post<{ status: string; output: { variants: PageVariant[] } | null }>(`/entities/${entity}/operations`, {
			capability: "page.adapt",
			version: 1,
			idempotency_key: crypto.randomUUID(),
			input: { page_id: page, platforms },
		}),
	/** Save an edit of a version; approving one is the page's to do once it is live. */
	saveVariant: (
		entity: string,
		variant: string,
		body: { expected_revision: number; fields: Record<string, string>; file_ids: string[]; target_url: string },
	) => post<PageVariant>(`/entities/${entity}/page-variants/${variant}`, body),
	pageEmbed: (page: string) => get<PageEmbed>(`/pages/${page}/embed`),
	savePage: (page: string, body: { title?: string; description?: string; blocks?: Block[]; revision?: number }) =>
		patch<{ revision: number }>(`/pages/${page}`, body),
	recipes: (entity: string, f: Filters) =>
		get<{ recipes: RecipeCard[]; targets: TargetCard[]; balance: number }>(`/entities/${entity}/recipes${query(f)}`),
	startGeneration: (
		entity: string,
		body: {
			recipe: string;
			template_id?: string;
			other_entity_id?: string;
			locale?: string;
			quality?: string;
			/** The gap the article is for; absent is the brand's default gap. */
			gap_id?: string;
		},
	) => post<{ job_id: string; operation_id: string }>(`/entities/${entity}/generate`, body),
	/** The gateway's model board. Served under the Agent's route table, but the
	 *  board is the platform's — both composers pick from the same one. */
	models: () =>
		get<{
			models: string[];
			defaultModel?: string;
			modelDetails?: { model: string; display_name: string }[];
		}>("/agent/providers/base/models"),
	revisePage: (page: string, ask: { feedback: string; quality?: string; model?: string; effort?: string }) =>
		post<{ job_id: string }>(`/pages/${page}/revise`, ask),
	approvePage: async (page: string, revision?: number) => {
		type Publication = { id: string; status: string; error_code: string | null };
		let operation = await post<Publication>(`/pages/${page}/approve`, { revision, request_id: crypto.randomUUID() });
		const deadline = Date.now() + 30_000;
		while (operation.status === "waiting" && Date.now() < deadline) {
			await new Promise((resolve) => setTimeout(resolve, 1000));
			operation = await get<Publication>(`/operations/${operation.id}`);
		}
		if (operation.status === "failed" || operation.status === "skipped") {
			throw new ApiError(409, "conflict", "pages.publishFailed");
		}
		return { status: operation.status === "done" ? "published" : "waiting", operation_id: operation.id };
	},
	unpublishPage: (page: string, reason?: string) => post<{ status: string }>(`/pages/${page}/unpublish`, { reason }),
	factcheck: (entity: string, f: InsightFilters) => get<ClaimRow[]>(`/entities/${entity}/factcheck${query(f)}`),
	reflows: (entity: string, f: InsightFilters) => get<ReflowRow[]>(`/entities/${entity}/reflows${query(f)}`),
	/** The brand's live pages, and the weekly workflow that measures them (null: the brand has none). */
	reflowPages: (entity: string, f: InsightFilters) =>
		get<{ pages: PageStanding[]; weekly_workflow_id: string | null }>(`/entities/${entity}/reflow/pages${query(f)}`),
	badge: (entity: string, locale: string) =>
		get<{
			token: string;
			svg_url: string;
			backlink_ok: boolean | null;
			backlink_checked_at: string | null;
			html: string;
			markdown: string;
		}>(`/entities/${entity}/badge${query({ locale })}`),
	collectFacts: (entity: string) => post<{ added: number }>(`/entities/${entity}/facts/collect`, {}),
	settings: (entity: string) => get<Settings>(`/entities/${entity}/settings`),
	saveSettings: (entity: string, body: Record<string, unknown>) =>
		patch<{ ok: boolean }>(`/entities/${entity}/settings`, body),
	sampleNow: (entity: string, f: Filters, questionIds?: string[]) =>
		post<{ queued: number; week: string; note: string | null; estimate_credits: number }>(
			`/entities/${entity}/sample/manual${query(f)}`,
			{ question_ids: questionIds, request_id: crypto.randomUUID() },
		),
	generationJobs: (entity: string, f: Filters) =>
		get<GenerationJob[]>(`/entities/${entity}/generation-jobs${query(f)}`),
	tasks: (org: string, filters: TaskFilters) => get<TaskList>(`/orgs/${org}/tasks${query(filters)}`),
	taskDetail: (id: string) => get<TaskDetail>(`/tasks/${id}`),
	pauseTask: (id: string) => post<{ ok: boolean }>(`/tasks/${id}/pause`, {}),
	resumeTask: (id: string) => post<{ ok: boolean }>(`/tasks/${id}/resume`, {}),
	retryTask: (id: string) => post<{ ok: boolean }>(`/tasks/${id}/retry`, {}),
	job: (job: string) => get<Record<string, unknown>>(`/jobs/${job}`),
};

/**
 * Where to land after signing in, or the root when the answer cannot be trusted.
 *
 * Only a path on this origin: `//evil.example` and `/\evil.example` are both
 * read by browsers as another host, and a sign-in page that forwards to
 * wherever its query string says is a credential-phishing primitive — the
 * mail says "sign in to Ondit", the page is ours, and the landing is not.
 */
export function safeNext(next: string | null | undefined): string {
	return next && /^\/(?![/\\])/.test(next) ? next : "/";
}

export function providerLoginUrl(provider: "google" | "github", next = "/"): string {
	return `${ORIGIN}/auth/oauth/${provider}?next=${encodeURIComponent(safeNext(next))}`;
}

/** The panel's own sign-in page. Where a 401 sends people. */
export function loginUrl(next = "/"): string {
	return `/login?next=${encodeURIComponent(next)}`;
}

export function logoutUrl(): string {
	return `${ORIGIN}/auth/logout`;
}

/** Sign out without leaving the page, for a screen that has somewhere of its
 *  own to go next — the link at `logoutUrl` always lands on the sign-in page. */
export function logout(): Promise<{ ok: boolean }> {
	return post<{ ok: boolean }>("/auth/logout");
}

/** Live events for an org, as an EventSource. */
export function events(org: string): EventSource {
	return new EventSource(`${ORIGIN}/api/v1/events?org=${encodeURIComponent(org)}`, {
		withCredentials: true,
	});
}
